// Each experience marker has a `googleMarker` property which is an `AdvancedMarkerElement` - meaning it has HTML content.
// The experience marker's element will have HTML class modifiers applied that allow us to use CSS rules to adjust the rendering of the marker.
//
// Marker Modifiers
// We add the `commercial`, `has_benefits` and `monetizable` modifiers as appropriate. This way we can target markers to render differently based
// on these characteristics eg. An experience marker which is `monetizable` and `has_benefits` might render larger and show the icons.
// See `app/stylesheets/online/apps/shared/_experience_marker.scss`
//
// Marker zIndex
// The zIndex for a google marker allows us to determine which marker appears on top.
// We adjust the zIndex according to a formula that ensures that monetizable experiences with benefits (experiences on a paid plan) will have a boosted zIndex and sit on top.
// In addition, we randomize the adjustment so that competing monetizable experiences with benefits will each have a chance of being on top.

import OnlineAppComponent from "../lib/online_app_component";
import Experience from "../models/experience";

export const ExperienceMarkerConfig = {
    baseHtmlClass: "experience_marker",
    selector: ".experience_marker",
    rankingScoreHtmlClass: "experience_marker__score",
};

export class ExperienceMarker extends OnlineAppComponent{

    constructor(experience, onlineApp){
        super(null, onlineApp, ExperienceMarkerConfig);
        this.element = ExperienceMarker.createElement(ExperienceMarkerConfig.baseHtmlClass);

        this.rawExperienceData = experience;
        this.experience = new Experience(experience, this, this.onlineApp);

        this.experienceId = experience['id'];
        this.experienceCode = experience['code'];
        this.regionCode = experience['region_code'];
        this.primaryName = experience['primary_name'];
        this.commercial = experience['commercial'];
        this.hasBenefits = experience['has_benefits'];
        this.monetizable = experience['monetizable'];
        this.experienceSlug = experience['slug'];
        this.rankingScore = experience['ranking_score'];
        this.rankingsCount = parseInt(experience['rankings_count'], 10);
        this.markerIconUrl = experience['marker_url'];
        this.lat = experience['coord']['lat'];
        this.long = experience['coord']['long'];
        this.markerIconLoaded = false;
        this.defaultMarkerIconUrl = '/images/online/apps/shared/map/default_marker.png';
        this.markerIconWidth = 32;
        this.markerIconHeight = 32;

        const markerOptions = {
            position: { lat: parseFloat(this.lat), lng: parseFloat(this.long) },
            title: this.primaryName,
            content: this.getContent(),
            zIndex: this.calcZIndex(),
            gmpClickable: true,
        };

        this.statsIdentifier = `${ experience['code'] || experience['id'] }`;

        if (!!this.commercial) { this.addHtmlClassState('commercial') }
        if (!!this.hasBenefits) { this.addHtmlClassState('has_benefits') }
        if (!!this.monetizable) { this.addHtmlClassState('monetizable') }

        // Initialize the marker with the default icon.
        this.googleMarker = new google.maps.marker.AdvancedMarkerElement(markerOptions);

        this.loadMarkerIconImage();

        // Marker taps select the experience. `gmpClickable` makes the AdvancedMarkerElement emit
        // `gmp-click` and keeps the tap from falling through to the map, so the old
        // OverlayView.preventMapHitsAndGesturesFrom + element click listener (RichMarker/OverlayView
        // concerns) are no longer needed.
        this.googleMarker.addListener('gmp-click', () => {
            this.dispatchAction('experienceMarkerSelected', this);
        });

    }

// Adding a marker is tricky because we don't know the size (px dimensions) of the marker icon in advance as these are dynamically generated on the server.
// However Google Maps wants to know the marker icon size when initializing the Marker.
// So we init the marker with just a default icon and then load the icon image.
// Once the image loads we get its size and dynamically update the marker's icon size.

// Fetch the marker icon image and once it has loaded use its dimensions to update the marker's icon.
    loadMarkerIconImage() {
        // set local vars for the image `onload` function to access.
        let markerIconImg = new Image();
        const thisExperienceMarker = this;
        markerIconImg.onload = function() {
            // `this` is a reference to the Image object that has just loaded.
            thisExperienceMarker.markerIconLoaded = true;
            // The height and width are halved to account for a @2x image.
            thisExperienceMarker.markerIconWidth = this.width/2;
            thisExperienceMarker.markerIconHeight = this.height/2;
            thisExperienceMarker.renderContent();
        };
        markerIconImg.src = this.markerIconUrl;
    }

    commercialWithBenefits() {
        return (!!this.commercial && !!this.hasBenefits);
    }

    monetizableWithBenefits() {
        return (!!this.monetizable && !!this.hasBenefits);
    }

    calcZIndex() {
        let zIndex = Math.abs(this.lat * 1000000); // Multiply the lat by 1M to get a abs integer that is sufficiently precise.

        // monetizable experiences with benefits will have a boosted zIndex and sit on top.
        // We randomize the adjustment to ensure that varying experiences get a turn on top.
        let adjustment = (this.monetizableWithBenefits()) ?
            (Math.round(Math.random() * 500000)) + 100000 : 0;

        return Math.round(Math.abs(zIndex + adjustment));
    }

    renderContent() {
        this.googleMarker.content = this.getContent();
    }

    getContent() {
        this.element.innerHTML = (this.markerIconLoaded) ? this.loadedMarkerContent() : this.unloadedMarkerContent();
        return this.element;
    }

    loadedMarkerContent() {
        return (`
            ${ this.markerIconContent() } 
            ${ this.rankingScoreContent() }
        `);
    }

    markerIconContent() {
        let styles = '';
        return `<img src="${ this.markerIconUrl }" height="${ this.markerIconHeight }" width="${ this.markerIconWidth }" style="${ styles }"/>`;
    }

    rankingScoreContent() {
        let styles = ``;
        if (this.monetizableWithBenefits()) {
            styles += `font-weight: bold;`
        }
        return !Number.isNaN(this.rankingsCount) && this.rankingsCount > 0
            ? `<div class="${ExperienceMarkerConfig.rankingScoreHtmlClass}" style="${styles}">${this.rankingScore}</div>`
            : "";
    }

    unloadedMarkerContent() {
        return `<img src="${ this.defaultMarkerIconUrl }" height="${ this.markerIconHeight }" width="${ this.markerIconWidth }" style=""/>`;
    }

}
